import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axiosClient, {
  IMPERSONATION_BACKUP_KEY,
  IMPERSONATION_META_KEY,
  invalidateAuthSession,
  getAuthGeneration,
  parseJwtClaims,
  REFRESH_TOKEN_KEY,
  refreshAccessToken,
  TOKEN_KEY,
} from "../../api/axiosClient";
import { toast } from "react-toastify";
import { stopAllSignalRConnections } from "../../hooks/useSignalR";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const LAST_ACTIVITY_KEY = "xb_last_activity_at";
const ACTIVITY_WRITE_THROTTLE_MS = 15 * 1000;
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;
const LOGOUT_STAMP_KEY = "xb_auth_logout_stamp";
const SESSION_STAMP_KEY = "xb_session_stamp";

const toPositiveNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const idleTimeoutMinutes =
  toPositiveNumber(process.env.REACT_APP_AUTH_IDLE_TIMEOUT_MINUTES) || 30;
const refreshLeadSeconds =
  toPositiveNumber(process.env.REACT_APP_AUTH_REFRESH_LEAD_SECONDS) || 300;

const IDLE_TIMEOUT_MS = idleTimeoutMinutes * 60 * 1000;
const REFRESH_LEAD_MS = refreshLeadSeconds * 1000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "scroll",
  "touchstart",
];

function readLastActivityAt() {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function clearLocalStorageByPrefix(prefix) {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach(key => localStorage.removeItem(key));
  } catch {
    // ignore storage errors
  }
}

// ---- local cache for entitlements (warm start only) ----
// We use this just to prefill UI quickly; we ALWAYS revalidate from server.
const ENTLS_KEY = bizId => `entitlements:${bizId}`;
const ENTLS_TTL_MS = 5 * 60 * 1000; // 5 minutes

const readCache = bizId => {
  try {
    const raw = localStorage.getItem(ENTLS_KEY(bizId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?._cachedAt) return null;
    if (Date.now() - parsed._cachedAt > ENTLS_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (bizId, snap) => {
  try {
    localStorage.setItem(
      ENTLS_KEY(bizId),
      JSON.stringify({ ...snap, _cachedAt: Date.now() })
    );
  } catch {
    // ignore storage errors
  }
};

// ---- selected business (platform scope -> business scope) ----
const SA_BIZ_ID_KEY = "sa_selectedBusinessId";
const SA_BIZ_NAME_KEY = "sa_selectedBusinessName";

function readSaSelection() {
  try {
    return {
      id: localStorage.getItem(SA_BIZ_ID_KEY) || null,
      name: localStorage.getItem(SA_BIZ_NAME_KEY) || null,
    };
  } catch {
    return { id: null, name: null };
  }
}

// ---- helpers for permissions & entitlements ----

// Normalize any permission/feature code to a canonical uppercase string
const normalizeCode = code => {
  if (!code) return "";
  return String(code).trim().toUpperCase();
};

const ROLE_KEYS = {
  PLATFORM_SUPER_ADMIN: "platform.super_admin",
  PLATFORM_PARTNER: "platform.partner",
  PLATFORM_RESELLER: "platform.reseller",
  BUSINESS_OWNER: "business.owner",
  BUSINESS_STAFF: "business.staff",
};

const normalizeRoleKey = value => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  return normalized || null;
};

const normalizeRoleScope = value => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "platform" || normalized === "business") return normalized;
  return null;
};

const normalizeLegacyRoleName = value => {
  const normalized = normalizeRoleKey(value);
  switch (normalized) {
    case "admin":
    case "superadmin":
      return ROLE_KEYS.PLATFORM_SUPER_ADMIN;
    case "partner":
      return ROLE_KEYS.PLATFORM_PARTNER;
    case "reseller":
      return ROLE_KEYS.PLATFORM_RESELLER;
    case "business":
    case "owner":
      return ROLE_KEYS.BUSINESS_OWNER;
    case "staff":
      return ROLE_KEYS.BUSINESS_STAFF;
    default:
      return null;
  }
};

const resolveRoleKey = (rawRoleKey, rawRoleName) =>
  normalizeRoleKey(rawRoleKey) || normalizeLegacyRoleName(rawRoleName);

const deriveRoleScopeFromRoleKey = roleKey => {
  switch (roleKey) {
    case ROLE_KEYS.PLATFORM_SUPER_ADMIN:
    case ROLE_KEYS.PLATFORM_PARTNER:
    case ROLE_KEYS.PLATFORM_RESELLER:
      return "platform";
    case ROLE_KEYS.BUSINESS_OWNER:
    case ROLE_KEYS.BUSINESS_STAFF:
      return "business";
    default:
      return null;
  }
};

const resolveRoleScope = (rawRoleScope, roleKey) =>
  normalizeRoleScope(rawRoleScope) || deriveRoleScopeFromRoleKey(roleKey);

const isLegacyPlatformRoleName = roleName =>
  ["admin", "superadmin", "partner", "reseller"].includes(
    String(roleName || "").trim().toLowerCase()
  );

const isLegacySuperAdminRoleName = roleName =>
  ["admin", "superadmin"].includes(
    String(roleName || "").trim().toLowerCase()
  );

function extractContextPermissions(perms) {
  if (!Array.isArray(perms)) return [];

  return perms
    .map(p => {
      if (typeof p === "string") return p;
      if (p && typeof p === "object") {
        return p.code || p.Code || p.permissionCode || p.PermissionCode || null;
      }
      return null;
    })
    .filter(Boolean);
}

// Normalize permissions coming from EntitlementsSnapshotDto
function extractEntitlementPermissions(entitlements) {
  if (!entitlements) return [];

  const raw =
    entitlements.GrantedPermissions ??
    entitlements.grantedPermissions ??
    entitlements.Permissions ??
    entitlements.permissions ??
    [];

  if (!Array.isArray(raw)) return [];

  return raw
    .map(p => {
      if (typeof p === "string") return p;
      if (p && typeof p === "object") {
        return p.code || p.Code || p.permissionCode || p.PermissionCode || null;
      }
      return null;
    })
    .filter(Boolean);
}

// "MESSAGING.SEND.TEXT" → "MESSAGING"
function getPermissionFamily(code) {
  const norm = normalizeCode(code);
  if (!norm) return null;
  const idx = norm.indexOf(".");
  if (idx === -1) return null;
  return norm.slice(0, idx);
}

// Plan-managed family rule: if entitlements contain any permission of the same family,
// that family is controlled by the plan.
function isPlanManagedPermission(code, entitlementPermsUpper) {
  const family = getPermissionFamily(code);
  if (!family) return false;

  const familyPrefix = family + ".";
  return (entitlementPermsUpper || []).some(p => p.startsWith(familyPrefix));
}

export default function AuthProvider({ children }) {
  const authContextRequestRef = useRef(0);
  // core auth
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [businessId, setBusinessId] = useState(null); // claim-derived for non-superadmin
  const [role, setRole] = useState(null);
  const [roleKey, setRoleKey] = useState(null);
  const [roleScope, setRoleScope] = useState(null);
  const [status, setStatus] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [hasAllAccess, setHasAllAccess] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState(null);
  const [impersonationReason, setImpersonationReason] = useState(null);
  const [impersonationStartedAt, setImpersonationStartedAt] = useState(null);

  // ✅ selected business scope
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState(null);

  // entitlements
  const [entitlements, setEntitlements] = useState(null);
  const [entLoading, setEntLoading] = useState(false);
  const [entError, setEntError] = useState(null);
  const lastActivityWriteAtRef = useRef(0);
  const lastHandledLogoutStampRef = useRef(0);

  // Restore the partner/reseller selection; superadmin clears legacy scope below.
  useEffect(() => {
    const { id, name } = readSaSelection();
    setSelectedBusinessId(id);
    setSelectedBusinessName(name);
  }, []);

  const setSuperAdminBusiness = useCallback(biz => {
    const canSelect = roleKey
      ? [ROLE_KEYS.PLATFORM_PARTNER, ROLE_KEYS.PLATFORM_RESELLER].includes(roleKey)
      : ["partner", "reseller"].includes(String(role || "").toLowerCase());
    if (!canSelect) return;

    const id = biz?.id || null;
    const name = biz?.name || biz?.businessName || null;

    setSelectedBusinessId(id);
    setSelectedBusinessName(name);

    try {
      if (id) localStorage.setItem(SA_BIZ_ID_KEY, id);
      else localStorage.removeItem(SA_BIZ_ID_KEY);

      if (name) localStorage.setItem(SA_BIZ_NAME_KEY, name);
      else localStorage.removeItem(SA_BIZ_NAME_KEY);
    } catch {}
  }, [role, roleKey]);

  const clearSuperAdminBusiness = useCallback(() => {
    setSelectedBusinessId(null);
    setSelectedBusinessName(null);
    try {
      localStorage.removeItem(SA_BIZ_ID_KEY);
      localStorage.removeItem(SA_BIZ_NAME_KEY);
    } catch {}
  }, []);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setBusiness(null);
    setBusinessId(null);
    setRole(null);
    setRoleKey(null);
    setRoleScope(null);
    setStatus(null);
    setPlanId(null);
    setHasAllAccess(false);
    setPermissions([]);
    setAvailableFeatures({});
    setIsImpersonating(false);
    setImpersonatedBy(null);
    setImpersonationReason(null);
    setImpersonationStartedAt(null);
    setEntitlements(null);
    setEntError(null);
    setEntLoading(false);
  }, []);

  const clearClientAuthBootstrap = useCallback(() => {
    try {
      localStorage.removeItem("xbytechat-auth-data");
      localStorage.removeItem("businessId");
    } catch {}
  }, []);

  const markSessionActivity = useCallback((force = false) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;

      if (!force && typeof document !== "undefined") {
        if (document.visibilityState === "hidden") return;
      }

      const now = Date.now();
      if (
        !force &&
        now - lastActivityWriteAtRef.current < ACTIVITY_WRITE_THROTTLE_MS
      ) {
        return;
      }

      lastActivityWriteAtRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // ignore storage errors
    }
  }, []);

  // ✅ Central logout: clears token + legacy keys + React state
  const logout = useCallback(
    async (options = {}) => {
      const {
        reason = null,
        redirectTo = null,
        silent = false,
        toastMessage = "You have been logged out.",
      } = options;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      invalidateAuthSession();
      stopAllSignalRConnections();

      try {
        if (refreshToken) {
          await axiosClient.post(
            "/auth/logout",
            { refreshToken },
            {
              __skipAuthRefresh: true,
              __silentToast: true,
              __silent401: true,
              __silent403: true,
              __silentGenericToast: true,
              timeout: 3000,
            }
          );
        }
      } catch {
        // Logout must complete locally even if the server revoke call fails.
      } finally {
        try {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(LAST_ACTIVITY_KEY);

          const legacyKeys = [
            "xbytechat-auth",
            "accessToken",
            "role",
            "plan",
            "businessId",
            "companyName",
            "xbytechat-auth-data",
            "userName",
            "email",
            "userAvatar",
          ];
          legacyKeys.forEach(k => localStorage.removeItem(k));

          localStorage.removeItem(SA_BIZ_ID_KEY);
          localStorage.removeItem(SA_BIZ_NAME_KEY);
          localStorage.removeItem(IMPERSONATION_META_KEY);
          clearLocalStorageByPrefix("entitlements:");

          try {
            localStorage.setItem(LOGOUT_STAMP_KEY, String(Date.now()));
          } catch {}

          sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
          sessionStorage.removeItem("last_quota_denial");

          try {
            const sessionKeysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i += 1) {
              const key = sessionStorage.key(i);
              if (key?.startsWith("sendTemplateInstant.formState.")) {
                sessionKeysToRemove.push(key);
              }
            }
            if (reason) {
              sessionStorage.setItem("auth_last_reason", reason);
            }
            sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
          } catch {}
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[Auth] Failed to clear token/keys on logout", err);
        }

        clearSuperAdminBusiness();
        clearAuthState();
        clearClientAuthBootstrap();
        setIsLoading(false);

        if (!silent && toastMessage) {
          try {
            toast.info(toastMessage);
          } catch {}
        }

        if (redirectTo) {
          window.location.assign(redirectTo);
        }
      }
    },
    [clearAuthState, clearClientAuthBootstrap, clearSuperAdminBusiness]
  );

  useEffect(() => {
    const handleStorage = event => {
      if (event.key !== LOGOUT_STAMP_KEY || !event.newValue) return;

      const logoutStamp = Number(event.newValue);
      if (!Number.isFinite(logoutStamp)) return;
      if (logoutStamp <= lastHandledLogoutStampRef.current) return;

      try {
        const sessionStamp = Number(localStorage.getItem(SESSION_STAMP_KEY));
        if (Number.isFinite(sessionStamp) && sessionStamp > logoutStamp) {
          return;
        }
      } catch {
        // ignore storage errors
      }

      lastHandledLogoutStampRef.current = logoutStamp;

      invalidateAuthSession();
      stopAllSignalRConnections();

      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem("xbytechat-auth");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("role");
        localStorage.removeItem("plan");
        localStorage.removeItem("businessId");
        localStorage.removeItem("companyName");
        localStorage.removeItem("xbytechat-auth-data");
        localStorage.removeItem("userName");
        localStorage.removeItem("email");
        localStorage.removeItem("userAvatar");
        localStorage.removeItem(SA_BIZ_ID_KEY);
        localStorage.removeItem(SA_BIZ_NAME_KEY);
        localStorage.removeItem(IMPERSONATION_META_KEY);
        clearLocalStorageByPrefix("entitlements:");
        sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
        sessionStorage.removeItem("last_quota_denial");
      } catch {
        // ignore storage errors
      }

      clearSuperAdminBusiness();
      clearAuthState();
      clearClientAuthBootstrap();
      setIsLoading(false);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clearAuthState, clearClientAuthBootstrap, clearSuperAdminBusiness]);

  const refreshAuthContext = useCallback(async ({ background = false } = {}) => {
    const requestId = ++authContextRequestRef.current;
    const generation = getAuthGeneration();
    const isCurrent = () => requestId === authContextRequestRef.current && generation === getAuthGeneration();
    if (!background) setIsLoading(true);
    try {
      const { data } = await axiosClient.get("/auth/context", {
        __silent401: true,
      });
      if (!isCurrent()) return null;

      const {
        isAuthenticated,
        user: u,
        business: b,
        role: r,
        roleKey: rk,
        roleScope: rs,
        status: s,
        hasAllAccess: haa,
        permissions: perms,
        features,
        businessId: ctxBusinessId,
        planId: ctxPlanId,
        isImpersonating: ctxIsImpersonating,
        impersonatedBy: ctxImpersonatedBy,
        impersonationReason: ctxImpersonationReason,
        impersonationStartedAt: ctxImpersonationStartedAt,
      } = data || {};

      if (!isAuthenticated) {
        clearAuthState();
        return null;
      }

      const nextRole = r || u?.role || u?.Role || null;
      const nextRoleKey = resolveRoleKey(
        rk || u?.roleKey || u?.RoleKey,
        nextRole
      );
      const nextRoleScope = resolveRoleScope(
        rs || u?.roleScope || u?.RoleScope,
        nextRoleKey
      );
      const nextPlanId =
        ctxPlanId || b?.planId || b?.PlanId || b?.planID || null;

      setUser(u || null);
      setBusiness(b || null);
      setRole(nextRole);
      setRoleKey(nextRoleKey || null);
      setRoleScope(nextRoleScope || null);
      setStatus(s || null);
      setPlanId(nextPlanId || null);
      setHasAllAccess(!!haa);
      setPermissions(extractContextPermissions(perms));
      setIsImpersonating(!!ctxIsImpersonating);
      setImpersonatedBy(ctxImpersonatedBy || null);
      setImpersonationReason(ctxImpersonationReason || null);
      setImpersonationStartedAt(ctxImpersonationStartedAt || null);

      const feat = Array.isArray(features)
        ? Object.fromEntries(features.map(code => [code, true]))
        : {};
      setAvailableFeatures(feat);

      // ✅ derive effective businessId (for normal business/staff)
      const nextIsSuperAdmin = nextRoleKey === ROLE_KEYS.PLATFORM_SUPER_ADMIN ||
        (!nextRoleKey && isLegacySuperAdminRoleName(nextRole));
      const effectiveBizId = nextIsSuperAdmin ? null : (
        ctxBusinessId ||
        b?.id ||
        b?.businessId ||
        b?.BusinessId ||
        u?.businessId ||
        u?.BusinessId ||
        null);

      setBusinessId(effectiveBizId || null);

      // store claim businessId (NOT superadmin selected scope)
      try {
        if (effectiveBizId) localStorage.setItem("businessId", effectiveBizId);
        else localStorage.removeItem("businessId");
      } catch {}

      return data;
    } catch (err) {
      if (!isCurrent()) return null;
      // eslint-disable-next-line no-console
      console.warn("[Auth] /auth/context failed", err);
      const invalidSession = err?.response?.status === 401;
      if (invalidSession) {
        invalidateAuthSession();
        stopAllSignalRConnections();
        clearSuperAdminBusiness();
        clearClientAuthBootstrap();
        setIsLoading(false);
      }
      if (!background || invalidSession) clearAuthState();
      return null;
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [clearAuthState, clearClientAuthBootstrap, clearSuperAdminBusiness]);

  useEffect(() => {
    refreshAuthContext();
  }, [refreshAuthContext]);

  // A refreshed user object is not user activity or a new session.
  const sessionUserId = user?.id || user?.Id || null;
  useEffect(() => {
    if (!sessionUserId || isImpersonating) return undefined;

    const handleActivity = () => {
      markSessionActivity(false);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        markSessionActivity(true);
      }
    };

    markSessionActivity(true);

    ACTIVITY_EVENTS.forEach(eventName => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach(eventName => {
        window.removeEventListener(eventName, handleActivity);
      });
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isImpersonating, markSessionActivity, sessionUserId]);

  useEffect(() => {
    if (!sessionUserId || isImpersonating) return undefined;

    let disposed = false;

    const checkSessionHealth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;

      const claims = parseJwtClaims(token);
      const isImpersonationToken =
        claims?.impersonation === true ||
        String(claims?.impersonation || "").toLowerCase() === "true";

      if (isImpersonationToken) return;

      const now = Date.now();
      const lastActivityAt = readLastActivityAt() || now;
      const idleForMs = now - lastActivityAt;

      if (idleForMs >= IDLE_TIMEOUT_MS) {
        if (!disposed) {
          logout({
            reason: "idle-timeout",
            redirectTo: "/login?reason=idle-timeout",
            silent: true,
            toastMessage: null,
          });
        }
        return;
      }

      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }

      const expSeconds = Number(claims?.exp);
      if (!Number.isFinite(expSeconds)) return;

      const expiresAtMs = expSeconds * 1000;
      if (expiresAtMs - now > REFRESH_LEAD_MS) return;

      try {
        await refreshAccessToken();
      } catch {
        if (!disposed) {
          logout({
            reason: "session-expired",
            redirectTo: "/login?reason=session-expired",
            silent: true,
            toastMessage: null,
          });
        }
      }
    };

    void checkSessionHealth();
    const intervalId = window.setInterval(() => {
      void checkSessionHealth();
    }, SESSION_CHECK_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [isImpersonating, logout, sessionUserId]);

  // Partner/reseller keep managed selection. Superadmin enters via impersonation.
  const legacyRoleName = String(role || "").toLowerCase();
  const isPlatformSuperAdmin =
    roleKey === ROLE_KEYS.PLATFORM_SUPER_ADMIN ||
    (!roleKey && isLegacySuperAdminRoleName(legacyRoleName));
  const isPlatformPartner =
    roleKey === ROLE_KEYS.PLATFORM_PARTNER ||
    (!roleKey && legacyRoleName === "partner");
  const isPlatformReseller =
    roleKey === ROLE_KEYS.PLATFORM_RESELLER ||
    (!roleKey && legacyRoleName === "reseller");
  const isPlatformRole =
    roleScope === "platform" ||
    (!roleScope && isLegacyPlatformRoleName(legacyRoleName));
  const canSelectManagedBusinessScope =
    isPlatformPartner || isPlatformReseller;
  const effectiveBusinessId = isPlatformSuperAdmin ? null :
    canSelectManagedBusinessScope ? selectedBusinessId || null : businessId || null;

  useEffect(() => {
    if (isPlatformSuperAdmin) clearSuperAdminBusiness();
  }, [isPlatformSuperAdmin, clearSuperAdminBusiness]);

  const startImpersonationSession = useCallback(
    payload => {
      const nextToken = String(payload?.token || "").trim();
      if (!nextToken) {
        throw new Error("Impersonation token was not returned.");
      }

      if (isImpersonating) {
        throw new Error("Nested impersonation is not allowed.");
      }

      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (!currentToken) {
        throw new Error("Admin session token is missing.");
      }

      if (
        sessionStorage.getItem(IMPERSONATION_BACKUP_KEY) ||
        localStorage.getItem(IMPERSONATION_META_KEY)
      ) {
        throw new Error("An impersonation session is already active.");
      }

      const meta = {
        businessId: payload?.businessId || null,
        businessName: payload?.businessName || null,
        email: payload?.email || null,
        reason: payload?.reason || null,
        startedAtUtc: payload?.startedAtUtc || new Date().toISOString(),
        expiresAtUtc: payload?.expiresAtUtc || null,
      };

      sessionStorage.setItem(IMPERSONATION_BACKUP_KEY, currentToken);
      localStorage.setItem(TOKEN_KEY, nextToken);
      localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify(meta));

      clearSuperAdminBusiness();
      clearClientAuthBootstrap();
      window.location.assign("/app/welcomepage");
    },
    [clearClientAuthBootstrap, clearSuperAdminBusiness, isImpersonating]
  );

  const restoreAdminSession = useCallback(
    (redirectTo = "/app/admin/approvals") => {
      const backupToken = sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);

      if (backupToken) {
        localStorage.setItem(TOKEN_KEY, backupToken);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }

      sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
      localStorage.removeItem(IMPERSONATION_META_KEY);
      clearSuperAdminBusiness();
      clearClientAuthBootstrap();

      if (backupToken) {
        window.location.assign(redirectTo);
        return;
      }

      window.location.assign("/login?reason=session-expired");
    },
    [clearClientAuthBootstrap, clearSuperAdminBusiness]
  );

  // Precompute normalized permission sets (performance + consistency)
  const ctxPermsUpper = useMemo(() => {
    return Array.isArray(permissions) ? permissions.map(normalizeCode) : [];
  }, [permissions]);

  const entPermsUpper = useMemo(() => {
    const raw = extractEntitlementPermissions(entitlements);
    return Array.isArray(raw) ? raw.map(normalizeCode) : [];
  }, [entitlements]);

  // ✅ can(): normalization-aware, plan-first for plan-managed families
  const can = useCallback(
    rawCode => {
      if (!rawCode) return true;

      // 🔓 Superadmin / full-access accounts bypass checks
      if (hasAllAccess) return true;

      const code = normalizeCode(rawCode);
      const hasRolePerm = ctxPermsUpper.includes(code);

      // INBOX permissions are security controls (role-managed, not plan-managed).
      if (code.startsWith("INBOX.")) return hasRolePerm;

      // Plan-managed family: must be in plan AND in role
      if (
        entitlements &&
        entPermsUpper.length > 0 &&
        isPlanManagedPermission(code, entPermsUpper)
      ) {
        return entPermsUpper.includes(code) && hasRolePerm;
      }

      // Non plan-managed: role permissions only
      return hasRolePerm;
    },
    [ctxPermsUpper, entitlements, entPermsUpper, hasAllAccess]
  );

  // 🔁 Entitlements fetch with race protection (important when superadmin switches fast)
  const entReqSeq = useRef(0);
  const entitlementsBusinessIdRef = useRef(null);
  const currentEntitlementScopeRef = useRef(null);
  currentEntitlementScopeRef.current = effectiveBusinessId;

  // ✅ SWR-style entitlements: warm-start from cache, ALWAYS revalidate from server
  const fetchEntitlements = useCallback(
    async (bizId, { useWarmCache = true } = {}) => {
      if (!bizId || bizId !== currentEntitlementScopeRef.current) return null;

      // Increment sequence; only latest response is allowed to write state.
      const seq = ++entReqSeq.current;
      const generation = getAuthGeneration();
      const isCurrent = () => entReqSeq.current === seq &&
        generation === getAuthGeneration() && currentEntitlementScopeRef.current === bizId;
      const isSameBusiness = entitlementsBusinessIdRef.current === bizId;

      // Clear previous entitlements immediately on business switch (prevents “wrong biz” UI flash)
      if (!isSameBusiness) {
        setEntitlements(null);
      }
      setEntError(null);

      // 1) Warm-start from cache
      if (useWarmCache) {
        const cached = readCache(bizId);
        if (cached && isCurrent()) {
          entitlementsBusinessIdRef.current = bizId;
          setEntitlements(cached);
        }
      }

      // 2) Always revalidate
      try {
        if (isCurrent()) setEntLoading(true);
        const { data } = await axiosClient.get(`/entitlements/${bizId}`);

        // Ignore stale responses
        if (!isCurrent()) return null;

        writeCache(bizId, data);
        entitlementsBusinessIdRef.current = bizId;
        setEntitlements(data);
        setEntError(null);
        return data;
      } catch (err) {
        if (!isCurrent()) return null;

        setEntError(err);
        // eslint-disable-next-line no-console
        console.warn("[Entitlements] fetch failed", err);
        return null;
      } finally {
        if (isCurrent()) setEntLoading(false);
      }
    },
    []
  );

  // ✅ hasFeature(): use entitlements.Features if present; otherwise treat feature code as permission code
  const hasFeature = useCallback(
    code => {
      if (!code) return true;

      const featuresList =
        entitlements &&
        (entitlements.Features ||
          entitlements.features ||
          entitlements.FeatureGrants ||
          entitlements.featureGrants);

      if (Array.isArray(featuresList)) {
        const f = featuresList.find(
          x =>
            x.code === code ||
            x.Code === code ||
            x.featureKey === code ||
            x.FeatureKey === code
        );
        if (f) {
          const allowed =
            f.allowed ??
            f.Allowed ??
            f.isAllowed ??
            f.IsAllowed ??
            f.enabled ??
            f.Enabled;
          return !!allowed;
        }
      }

      if (can(code)) return true;
      return !!availableFeatures[code];
    },
    [entitlements, availableFeatures, can]
  );

  // ✅ getQuota(): align with EntitlementsSnapshotDto.Quotas shape
  const getQuota = useCallback(
    code => {
      const quotas =
        (entitlements &&
          (entitlements.Quotas ||
            entitlements.quotas ||
            entitlements.planQuotas)) ||
        [];

      if (!Array.isArray(quotas) || !code) {
        return { quotaKey: code, limit: 0, used: 0, remaining: 0 };
      }

      const q = quotas.find(
        x =>
          x.quotaKey === code ||
          x.QuotaKey === code ||
          x.code === code ||
          x.Code === code
      );

      if (!q) {
        return { quotaKey: code, limit: 0, used: 0, remaining: 0 };
      }

      const quotaKey = q.quotaKey ?? q.QuotaKey ?? code;
      const limit = q.limit ?? q.Limit ?? q.max ?? q.Max ?? null;
      const used = q.used ?? q.Used ?? q.consumed ?? q.Consumed ?? 0;

      let remaining = q.remaining ?? q.Remaining ?? null;
      if (remaining == null && limit != null) {
        remaining = Math.max(0, limit - used);
      }

      return { ...q, quotaKey, limit, used, remaining };
    },
    [entitlements]
  );

  const canUseQuota = useCallback(
    (code, amount = 1) => {
      if (!entitlements) return { ok: false, reason: "no-entitlements" };
      if (!hasFeature(code)) return { ok: false, reason: "no-feature" };

      const q = getQuota(code);

      // limit null => unlimited; limit 0 => blocked
      if (q.limit === 0) return { ok: false, reason: "quota-exceeded" };
      if (q.limit != null && q.remaining < amount)
        return { ok: false, reason: "quota-exceeded" };

      return { ok: true };
    },
    [entitlements, hasFeature, getQuota]
  );

  // 🔄 Explicit manual refresh → always network
  const refreshEntitlements = useCallback(async () => {
    if (effectiveBusinessId) {
      return fetchEntitlements(effectiveBusinessId, { useWarmCache: false });
    }
    return null;
  }, [effectiveBusinessId, fetchEntitlements]);

  // Initial load: warm from cache if available, then revalidate
  useEffect(() => {
    if (effectiveBusinessId) {
      fetchEntitlements(effectiveBusinessId, { useWarmCache: true }).catch(
        () => {}
      );
    } else {
      // clear state when scope becomes unknown
      setEntitlements(null);
      setEntError(null);
      setEntLoading(false);
    }
  }, [effectiveBusinessId, fetchEntitlements]);

  // Keep permissions fresh after admin updates role permissions (refresh on focus/visibility)
  const lastEntRefreshAtRef = useRef(0);
  useEffect(() => {
    const MIN_MS = 30 * 1000;

    const maybeRefresh = () => {
      if (!user || document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastEntRefreshAtRef.current < MIN_MS) return;
      lastEntRefreshAtRef.current = now;
      refreshAuthContext({ background: true })
        .then(data => data ? refreshEntitlements() : null).catch(() => {});
    };

    const onFocus = () => maybeRefresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") maybeRefresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = window.setInterval(maybeRefresh, MIN_MS);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshAuthContext, refreshEntitlements, user]);

  const userName =
    user?.name ||
    user?.fullName ||
    user?.displayName ||
    business?.businessName ||
    business?.name ||
    business?.companyName ||
    null;

  const value = useMemo(
    () => ({
      // core
      isLoading,
      user,
      business,
      businessId, // claim-derived (normal users)
      role,
      roleKey,
      roleScope,
      status,
      planId,
      hasAllAccess,
      permissions,
      availableFeatures,
      can,
      hasPermission: can,
      refreshAuthContext,
      userName,
      logout,
      isPlatformRole,
      isPlatformSuperAdmin,
      isPlatformPartner,
      isPlatformReseller,
      canSelectManagedBusinessScope,
      isImpersonating,
      impersonatedBy,
      impersonationReason,
      impersonationStartedAt,

      // Managed partner/reseller selection + effective scope
      selectedBusinessId: canSelectManagedBusinessScope ? selectedBusinessId : null,
      selectedBusinessName: canSelectManagedBusinessScope ? selectedBusinessName : null,
      setSuperAdminBusiness,
      clearSuperAdminBusiness,
      effectiveBusinessId,
      startImpersonationSession,
      restoreAdminSession,

      // entitlements
      entitlements,
      entLoading,
      entitlementsLoading: entLoading,
      entError,
      hasFeature,
      getQuota,
      canUseQuota,
      refreshEntitlements,
    }),
    [
      isLoading,
      user,
      business,
      businessId,
      role,
      roleKey,
      roleScope,
      status,
      planId,
      hasAllAccess,
      permissions,
      availableFeatures,
      can,
      refreshAuthContext,
      userName,
      logout,
      isPlatformRole,
      isPlatformSuperAdmin,
      isPlatformPartner,
      isPlatformReseller,
      canSelectManagedBusinessScope,
      isImpersonating,
      impersonatedBy,
      impersonationReason,
      impersonationStartedAt,
      selectedBusinessId,
      selectedBusinessName,
      setSuperAdminBusiness,
      clearSuperAdminBusiness,
      effectiveBusinessId,
      startImpersonationSession,
      restoreAdminSession,
      entitlements,
      entLoading,
      entError,
      hasFeature,
      getQuota,
      canUseQuota,
      refreshEntitlements,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
//       // main JWT
//       localStorage.removeItem(TOKEN_KEY);

//       // legacy auth keys (from previous iterations)
//       const legacyKeys = [
//         "xbytechat-auth",
//         "accessToken",
//         "role",
//         "plan",
//         "businessId",
//         "companyName",
//         "xbytechat-auth-data",
//       ];
//       legacyKeys.forEach(k => localStorage.removeItem(k));

//       // also clear superadmin business selection on logout (avoid scope leakage)
//       localStorage.removeItem(SA_BIZ_ID_KEY);
//       localStorage.removeItem(SA_BIZ_NAME_KEY);
//     } catch (err) {
//       // eslint-disable-next-line no-console
//       console.warn("[Auth] Failed to clear token/keys on logout", err);
//     }

//     clearSuperAdminBusiness();
//     clearAuthState();
//     setIsLoading(false);

//     try {
//       toast.info("You have been logged out.");
//     } catch {
//       // ignore toast failures
//     }
//   }, [clearAuthState, clearSuperAdminBusiness]);

//   const refreshAuthContext = useCallback(async () => {
//     setIsLoading(true);
//     try {
//       // Cookie-first auth: do not require a localStorage token to attempt context refresh.
//       // If a token exists, axiosClient will still attach it as a fallback.
//       const { data } = await axiosClient.get("/auth/context", {
//         __silent401: true,
//       });
//       const {
//         isAuthenticated,
//         user: u,
//         business: b,
//         role: r,
//         status: s,
//         hasAllAccess: haa,
//         permissions: perms,
//         features,
//         businessId: ctxBusinessId, // ✅ pick businessId if backend returns it top-level
//       } = data || {};

//       if (!isAuthenticated) {
//         clearAuthState();
//         return null;
//       }

//       setUser(u || null);
//       setBusiness(b || null);
//       setRole(r || null);
//       setStatus(s || null);
//       setHasAllAccess(!!haa);
//       setPermissions(extractContextPermissions(perms));
//       const feat = Array.isArray(features)
//         ? Object.fromEntries(features.map(code => [code, true]))
//         : {};
//       setAvailableFeatures(feat);

//       // ✅ derive effective businessId from context + nested objects
//       const effectiveBizId =
//         ctxBusinessId ||
//         b?.id ||
//         b?.businessId ||
//         b?.BusinessId ||
//         u?.businessId ||
//         u?.BusinessId ||
//         null;

//       setBusinessId(effectiveBizId || null);
//       if (effectiveBizId) {
//         try {
//           localStorage.setItem("businessId", effectiveBizId);
//         } catch {
//           // ignore storage error
//         }
//       }

//       return data;
//     } catch (err) {
//       // eslint-disable-next-line no-console
//       console.warn("[Auth] /auth/context failed", err);
//       clearAuthState();
//       return null;
//     } finally {
//       setIsLoading(false);
//     }
//   }, [clearAuthState]);

//   useEffect(() => {
//     refreshAuthContext();
//   }, [refreshAuthContext]);

//   // ✅ effective business scope (superadmin uses selectedBusinessId; others use claim businessId)
//   const roleKey = String(role || "").toLowerCase();
//   const effectiveBusinessId =
//     roleKey === "superadmin" ? selectedBusinessId || null : businessId || null;

//   // ✅ can(): normalization-aware, plan-first for plan-managed families
//   const can = useCallback(
//     rawCode => {
//       if (!rawCode) return true;

//       // 🔓 Superadmin / full-access accounts bypass plan checks
//       if (hasAllAccess) return true;

//       const code = normalizeCode(rawCode);

//       // Role/context permissions (from /auth/context)
//       const ctxPermsNorm = Array.isArray(permissions)
//         ? permissions.map(normalizeCode)
//         : [];

//       // Plan permissions (from entitlements snapshot)
//       const entPermsRaw = extractEntitlementPermissions(entitlements);
//       const entPermsNorm = Array.isArray(entPermsRaw)
//         ? entPermsRaw.map(normalizeCode)
//         : [];

//       // Role permission is always required unless hasAllAccess.
//       const hasRolePerm = ctxPermsNorm.includes(code);

//       // INBOX permissions are security controls (role-managed, not plan-managed).
//       if (code.startsWith("INBOX.")) return hasRolePerm;

//       // ✅ If we have entitlements, treat plan as an outer "ceiling" for plan-managed families
//       if (
//         entitlements &&
//         entPermsNorm.length > 0 &&
//         isPlanManagedPermission(code, entPermsNorm)
//       ) {
//         return entPermsNorm.includes(code) && hasRolePerm;
//       }

//       // Non plan-managed codes: fall back to role/context permissions
//       return hasRolePerm;
//     },
//     [permissions, entitlements, hasAllAccess]
//   );

//   // ✅ SWR-style entitlements: warm-start from cache, ALWAYS revalidate from server
//   const fetchEntitlements = useCallback(
//     async (bizId, { useWarmCache = true } = {}) => {
//       if (!bizId) return null;

//       // 1) Warm-start from cache for faster initial render
//       if (useWarmCache) {
//         const cached = readCache(bizId);
//         if (cached) {
//           setEntitlements(cached);
//         }
//       }

//       // 2) Always revalidate from backend (big-player style)
//       try {
//         setEntLoading(true);
//         setEntError(null);
//         const { data } = await axiosClient.get(`/entitlements/${bizId}`);

//         writeCache(bizId, data);
//         setEntitlements(data);
//         return data;
//       } catch (err) {
//         setEntError(err);
//         // eslint-disable-next-line no-console
//         console.warn("[Entitlements] fetch failed", err);
//         return null;
//       } finally {
//         setEntLoading(false);
//       }
//     },
//     []
//   );

//   // ✅ hasFeature(): use entitlements.Features if present; otherwise treat feature code as permission code
//   const hasFeature = useCallback(
//     code => {
//       if (!code) return true;

//       // 1) If snapshot has explicit Features list, use that
//       const featuresList =
//         entitlements &&
//         (entitlements.Features ||
//           entitlements.features ||
//           entitlements.FeatureGrants ||
//           entitlements.featureGrants);

//       if (Array.isArray(featuresList)) {
//         const f = featuresList.find(
//           x =>
//             x.code === code ||
//             x.Code === code ||
//             x.featureKey === code ||
//             x.FeatureKey === code
//         );
//         if (f) {
//           const allowed =
//             f.allowed ??
//             f.Allowed ??
//             f.isAllowed ??
//             f.IsAllowed ??
//             f.enabled ??
//             f.Enabled;
//           return !!allowed;
//         }
//       }

//       // 2) Otherwise, fall back to permission-based gating
//       if (can(code)) return true;

//       // 3) Finally, fall back to older /auth/context "features" map
//       return !!availableFeatures[code];
//     },
//     [entitlements, availableFeatures, can]
//   );

//   // ✅ getQuota(): align with EntitlementsSnapshotDto.Quotas shape
//   const getQuota = useCallback(
//     code => {
//       const quotas =
//         (entitlements &&
//           (entitlements.Quotas ||
//             entitlements.quotas ||
//             entitlements.planQuotas)) ||
//         [];

//       if (!Array.isArray(quotas) || !code) {
//         return { quotaKey: code, limit: 0, used: 0, remaining: 0 };
//       }

//       const q = quotas.find(
//         x =>
//           x.quotaKey === code ||
//           x.QuotaKey === code ||
//           x.code === code ||
//           x.Code === code
//       );

//       if (!q) {
//         return { quotaKey: code, limit: 0, used: 0, remaining: 0 };
//       }

//       const quotaKey = q.quotaKey ?? q.QuotaKey ?? code;
//       const limit = q.limit ?? q.Limit ?? q.max ?? q.Max ?? null;
//       const used = q.used ?? q.Used ?? q.consumed ?? q.Consumed ?? 0;

//       let remaining = q.remaining ?? q.Remaining ?? null;
//       if (remaining == null && limit != null) {
//         remaining = Math.max(0, limit - used);
//       }

//       return {
//         ...q,
//         quotaKey,
//         limit,
//         used,
//         remaining,
//       };
//     },
//     [entitlements]
//   );

//   const canUseQuota = useCallback(
//     (code, amount = 1) => {
//       if (!entitlements) return { ok: false, reason: "no-entitlements" };
//       if (!hasFeature(code)) return { ok: false, reason: "no-feature" };
//       const q = getQuota(code);
//       if (q.limit == null || q.limit === 0) {
//         return { ok: false, reason: "quota-exceeded" };
//       }
//       if (q.remaining < amount) {
//         return { ok: false, reason: "quota-exceeded" };
//       }
//       return { ok: true };
//     },
//     [entitlements, hasFeature, getQuota]
//   );

//   // 🔄 Explicit manual refresh → always network, no warm-only mode
//   const refreshEntitlements = useCallback(async () => {
//     // IMPORTANT:
//     // - Business users: use claim businessId
//     // - Superadmin: use selectedBusinessId (effectiveBusinessId)
//     if (effectiveBusinessId) {
//       return fetchEntitlements(effectiveBusinessId, { useWarmCache: false });
//     }
//     return null;
//   }, [effectiveBusinessId, fetchEntitlements]);

//   // Initial load: warm from cache if available, then revalidate
//   useEffect(() => {
//     if (effectiveBusinessId) {
//       fetchEntitlements(effectiveBusinessId, { useWarmCache: true }).catch(
//         () => {}
//       );
//     } else {
//       setEntitlements(null);
//     }
//   }, [effectiveBusinessId, fetchEntitlements]);

//   // Keep permissions fresh after admin updates role permissions (MVP: refresh on focus/visibility)
//   const lastEntRefreshAtRef = useRef(0);
//   useEffect(() => {
//     const MIN_MS = 30 * 1000;

//     const maybeRefresh = () => {
//       const now = Date.now();
//       if (now - lastEntRefreshAtRef.current < MIN_MS) return;
//       lastEntRefreshAtRef.current = now;
//       refreshEntitlements().catch(() => {});
//     };

//     const onFocus = () => maybeRefresh();
//     const onVisibilityChange = () => {
//       if (document.visibilityState === "visible") maybeRefresh();
//     };

//     window.addEventListener("focus", onFocus);
//     document.addEventListener("visibilitychange", onVisibilityChange);
//     return () => {
//       window.removeEventListener("focus", onFocus);
//       document.removeEventListener("visibilitychange", onVisibilityChange);
//     };
//   }, [refreshEntitlements]);

//   const userName =
//     business?.businessName ||
//     business?.name ||
//     business?.companyName ||
//     user?.fullName ||
//     user?.name ||
//     user?.displayName ||
//     null;

//   const value = useMemo(
//     () => ({
//       // core
//       isLoading,
//       user,
//       business,
//       businessId, // claim-derived
//       role,
//       status,
//       hasAllAccess,
//       permissions,
//       availableFeatures,
//       can,
//       refreshAuthContext,
//       userName,
//       logout,

//       // ✅ superadmin selection + effective scope
//       selectedBusinessId,
//       selectedBusinessName,
//       setSuperAdminBusiness,
//       clearSuperAdminBusiness,
//       effectiveBusinessId,

//       // entitlements
//       entitlements,
//       entLoading,
//       entError,
//       hasFeature,
//       getQuota,
//       canUseQuota,
//       refreshEntitlements,
//     }),
//     [
//       isLoading,
//       user,
//       business,
//       businessId,
//       role,
//       status,
//       hasAllAccess,
//       permissions,
//       availableFeatures,
//       can,
//       refreshAuthContext,
//       userName,
//       logout,
//       selectedBusinessId,
//       selectedBusinessName,
//       setSuperAdminBusiness,
//       clearSuperAdminBusiness,
//       effectiveBusinessId,
//       entitlements,
//       entLoading,
//       entError,
//       hasFeature,
//       getQuota,
//       canUseQuota,
//       refreshEntitlements,
//     ]
//   );

//   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
// }

// // 📄 File: src/app/providers/AuthProvider.jsx
// import React, {
//   createContext,
//   useCallback,
//   useContext,
//   useEffect,
//   useMemo,
//   useRef,
//   useState,
// } from "react";
// import axiosClient, { TOKEN_KEY } from "../../api/axiosClient";
// import { toast } from "react-toastify";

// const AuthContext = createContext(null);
// export const useAuth = () => useContext(AuthContext);

// // ---- local cache for entitlements (warm start only) ----
// // We use this just to prefill UI quickly; we ALWAYS revalidate from server.
// const ENTLS_KEY = bizId => `entitlements:${bizId}`;
// // TTL only controls how old warm-start data can be, it no longer skips network.
// const ENTLS_TTL_MS = 5 * 60 * 1000; // 5 minutes

// const readCache = bizId => {
//   try {
//     const raw = localStorage.getItem(ENTLS_KEY(bizId));
//     if (!raw) return null;
//     const parsed = JSON.parse(raw);
//     if (!parsed?._cachedAt) return null;
//     if (Date.now() - parsed._cachedAt > ENTLS_TTL_MS) return null;
//     return parsed;
//   } catch {
//     return null;
//   }
// };

// const writeCache = (bizId, snap) => {
//   try {
//     localStorage.setItem(
//       ENTLS_KEY(bizId),
//       JSON.stringify({ ...snap, _cachedAt: Date.now() })
//     );
//   } catch {
//     // ignore storage errors
//   }
// };

// // ---- helpers for permissions & entitlements ----

// // Normalize any permission/feature code to a canonical uppercase string
// const normalizeCode = code => {
//   if (!code) return "";
//   return String(code).trim().toUpperCase();
// };

// function extractContextPermissions(perms) {
//   if (!Array.isArray(perms)) return [];

//   return perms
//     .map(p => {
//       if (typeof p === "string") return p;
//       if (p && typeof p === "object") {
//         return p.code || p.Code || p.permissionCode || p.PermissionCode || null;
//       }
//       return null;
//     })
//     .filter(Boolean);
// }

// // Normalize permissions coming from EntitlementsSnapshotDto
// function extractEntitlementPermissions(entitlements) {
//   if (!entitlements) return [];

//   const raw =
//     entitlements.GrantedPermissions ??
//     entitlements.grantedPermissions ??
//     entitlements.Permissions ??
//     entitlements.permissions ??
//     [];

//   if (!Array.isArray(raw)) return [];

//   // Normalize to array of strings (but not uppercased yet)
//   return raw
//     .map(p => {
//       if (typeof p === "string") return p;
//       if (p && typeof p === "object") {
//         return p.code || p.Code || p.permissionCode || p.PermissionCode || null;
//       }
//       return null;
//     })
//     .filter(Boolean);
// }

// // Get the "family" of a permission code, e.g.
// // "MESSAGING.SEND.TEXT" → "MESSAGING"
// function getPermissionFamily(code) {
//   const norm = normalizeCode(code);
//   if (!norm) return null;
//   const idx = norm.indexOf(".");
//   if (idx === -1) return null;
//   return norm.slice(0, idx);
// }

// // Decide if a code should be considered "plan-managed".
// // Rule: if entitlements contain *any* permission of the same family,
// // that family is controlled by the plan.
// //
// // NOTE: This expects entitlementPerms to already be normalized (uppercase).
// function isPlanManagedPermission(code, entitlementPerms) {
//   const family = getPermissionFamily(code);
//   if (!family) return false;

//   const familyPrefix = family + ".";
//   return (entitlementPerms || []).some(p => p.startsWith(familyPrefix));
// }

// export default function AuthProvider({ children }) {
//   // core auth
//   const [isLoading, setIsLoading] = useState(true);
//   const [user, setUser] = useState(null);
//   const [business, setBusiness] = useState(null);
//   const [businessId, setBusinessId] = useState(null); // ✅ explicit businessId for frontend
//   const [role, setRole] = useState(null);
//   const [status, setStatus] = useState(null);
//   const [hasAllAccess, setHasAllAccess] = useState(false);
//   const [permissions, setPermissions] = useState([]);
//   const [availableFeatures, setAvailableFeatures] = useState({});

//   // entitlements
//   const [entitlements, setEntitlements] = useState(null);
//   const [entLoading, setEntLoading] = useState(false);
//   const [entError, setEntError] = useState(null);

//   const clearAuthState = useCallback(() => {
//     setUser(null);
//     setBusiness(null);
//     setBusinessId(null); // ✅ clear businessId
//     setRole(null);
//     setStatus(null);
//     setHasAllAccess(false);
//     setPermissions([]);
//     setAvailableFeatures({});
//     setEntitlements(null); // 🔁 also clear entitlements on logout
//   }, []);

//   // ✅ Central logout: clears token + legacy keys + React state
//   const logout = useCallback(() => {
//     try {
//       // main JWT
//       localStorage.removeItem(TOKEN_KEY);

//       // legacy auth keys (from previous iterations)
//       const legacyKeys = [
//         "xbytechat-auth",
//         "accessToken",
//         "role",
//         "plan",
//         "businessId",
//         "companyName",
//         "xbytechat-auth-data",
//       ];
//       legacyKeys.forEach(k => localStorage.removeItem(k));
//     } catch (err) {
//       // eslint-disable-next-line no-console
//       console.warn("[Auth] Failed to clear token/keys on logout", err);
//     }

//     clearAuthState();
//     setIsLoading(false);

//     try {
//       toast.info("You have been logged out.");
//     } catch {
//       // ignore toast failures
//     }
//   }, [clearAuthState]);

//   const refreshAuthContext = useCallback(async () => {
//     setIsLoading(true);
//     try {
//       // Cookie-first auth: do not require a localStorage token to attempt context refresh.
//       // If a token exists, axiosClient will still attach it as a fallback.
//       const { data } = await axiosClient.get("/auth/context", {
//         __silent401: true,
//       });
//       const {
//         isAuthenticated,
//         user: u,
//         business: b,
//         role: r,
//         status: s,
//         hasAllAccess: haa,
//         permissions: perms,
//         features,
//         businessId: ctxBusinessId, // ✅ pick businessId if backend returns it top-level
//       } = data || {};

//       if (!isAuthenticated) {
//         clearAuthState();
//         return null;
//       }

//       setUser(u || null);
//       setBusiness(b || null);
//       setRole(r || null);
//       setStatus(s || null);
//       setHasAllAccess(!!haa);
//       setPermissions(extractContextPermissions(perms));
//       const feat = Array.isArray(features)
//         ? Object.fromEntries(features.map(code => [code, true]))
//         : {};
//       setAvailableFeatures(feat);

//       // ✅ derive effective businessId from context + nested objects
//       const effectiveBizId =
//         ctxBusinessId ||
//         b?.id ||
//         b?.businessId ||
//         b?.BusinessId ||
//         u?.businessId ||
//         u?.BusinessId ||
//         null;

//       setBusinessId(effectiveBizId || null);
//       if (effectiveBizId) {
//         try {
//           localStorage.setItem("businessId", effectiveBizId);
//         } catch {
//           // ignore storage error
//         }
//       }

//       return data;
//     } catch (err) {
//       // eslint-disable-next-line no-console
//       console.warn("[Auth] /auth/context failed", err);
//       clearAuthState();
//       return null;
//     } finally {
//       setIsLoading(false);
//     }
//   }, [clearAuthState]);

//   useEffect(() => {
//     refreshAuthContext();
//   }, [refreshAuthContext]);

//   // ✅ can(): normalization-aware, plan-first for plan-managed families

//   const can = useCallback(
//     rawCode => {
//       if (!rawCode) return true;

//       // 🔓 Superadmin / full-access accounts bypass plan checks
//       if (hasAllAccess) return true;

//       const code = normalizeCode(rawCode);

//       // Role/context permissions (from /auth/context)
//       const ctxPermsNorm = Array.isArray(permissions)
//         ? permissions.map(normalizeCode)
//         : [];

//       // Plan permissions (from entitlements snapshot)
//       const entPermsRaw = extractEntitlementPermissions(entitlements);
//       const entPermsNorm = Array.isArray(entPermsRaw)
//         ? entPermsRaw.map(normalizeCode)
//         : [];

//       // Role permission is always required unless hasAllAccess.
//       const hasRolePerm = ctxPermsNorm.includes(code);

//       // INBOX permissions are security controls (role-managed, not plan-managed).
//       if (code.startsWith("INBOX.")) return hasRolePerm;

//       // ✅ If we have entitlements, treat plan as an outer "ceiling" for plan-managed families
//       if (
//         entitlements &&
//         entPermsNorm.length > 0 &&
//         isPlanManagedPermission(code, entPermsNorm)
//       ) {
//         return entPermsNorm.includes(code) && hasRolePerm;
//       }

//       // Non plan-managed codes: fall back to role/context permissions
//       return hasRolePerm;
//     },
//     [permissions, entitlements, hasAllAccess]
//   );

//   // ✅ SWR-style entitlements: warm-start from cache, ALWAYS revalidate from server
//   const fetchEntitlements = useCallback(
//     async (bizId, { useWarmCache = true } = {}) => {
//       if (!bizId) return null;

//       // 1) Warm-start from cache for faster initial render
//       if (useWarmCache) {
//         const cached = readCache(bizId);
//         if (cached) {
//           setEntitlements(cached);
//         }
//       }

//       // 2) Always revalidate from backend (big-player style)
//       try {
//         setEntLoading(true);
//         setEntError(null);
//         const { data } = await axiosClient.get(`/entitlements/${bizId}`);

//         // Optional: if you later add UpdatedAtUtc, you can compare here
//         writeCache(bizId, data);
//         setEntitlements(data);
//         return data;
//       } catch (err) {
//         setEntError(err);
//         // eslint-disable-next-line no-console
//         console.warn("[Entitlements] fetch failed", err);
//         return null;
//       } finally {
//         setEntLoading(false);
//       }
//     },
//     []
//   );

//   // ✅ hasFeature(): use entitlements.Features if present; otherwise treat feature code as permission code
//   const hasFeature = useCallback(
//     code => {
//       if (!code) return true;

//       // 1) If snapshot has explicit Features list, use that
//       const featuresList =
//         entitlements &&
//         (entitlements.Features ||
//           entitlements.features ||
//           entitlements.FeatureGrants ||
//           entitlements.featureGrants);

//       if (Array.isArray(featuresList)) {
//         const f = featuresList.find(
//           x =>
//             x.code === code ||
//             x.Code === code ||
//             x.featureKey === code ||
//             x.FeatureKey === code
//         );
//         if (f) {
//           const allowed =
//             f.allowed ??
//             f.Allowed ??
//             f.isAllowed ??
//             f.IsAllowed ??
//             f.enabled ??
//             f.Enabled;
//           return !!allowed;
//         }
//       }

//       // 2) Otherwise, fall back to permission-based gating
//       if (can(code)) return true;

//       // 3) Finally, fall back to older /auth/context "features" map
//       return !!availableFeatures[code];
//     },
//     [entitlements, availableFeatures, can]
//   );

//   // ✅ getQuota(): align with EntitlementsSnapshotDto.Quotas shape
//   const getQuota = useCallback(
//     code => {
//       const quotas =
//         (entitlements &&
//           (entitlements.Quotas ||
//             entitlements.quotas ||
//             entitlements.planQuotas)) ||
//         [];

//       if (!Array.isArray(quotas) || !code) {
//         return { quotaKey: code, limit: 0, used: 0, remaining: 0 };
//       }

//       const q = quotas.find(
//         x =>
//           x.quotaKey === code ||
//           x.QuotaKey === code ||
//           x.code === code ||
//           x.Code === code
//       );

//       if (!q) {
//         return { quotaKey: code, limit: 0, used: 0, remaining: 0 };
//       }

//       const quotaKey = q.quotaKey ?? q.QuotaKey ?? code;

//       const limit = q.limit ?? q.Limit ?? q.max ?? q.Max ?? null;

//       const used = q.used ?? q.Used ?? q.consumed ?? q.Consumed ?? 0;

//       let remaining = q.remaining ?? q.Remaining ?? null;

//       if (remaining == null && limit != null) {
//         remaining = Math.max(0, limit - used);
//       }

//       return {
//         ...q,
//         quotaKey,
//         limit,
//         used,
//         remaining,
//       };
//     },
//     [entitlements]
//   );

//   const canUseQuota = useCallback(
//     (code, amount = 1) => {
//       if (!entitlements) return { ok: false, reason: "no-entitlements" };
//       if (!hasFeature(code)) return { ok: false, reason: "no-feature" };
//       const q = getQuota(code);
//       if (q.limit == null || q.limit === 0) {
//         return { ok: false, reason: "quota-exceeded" };
//       }
//       if (q.remaining < amount) {
//         return { ok: false, reason: "quota-exceeded" };
//       }
//       return { ok: true };
//     },
//     [entitlements, hasFeature, getQuota]
//   );

//   // 🔄 Explicit manual refresh → always network, no warm-only mode
//   const refreshEntitlements = useCallback(async () => {
//     const entBizId = business?.id || businessId;
//     if (entBizId) {
//       return fetchEntitlements(entBizId, { useWarmCache: false });
//     }
//     return null;
//   }, [business?.id, businessId, fetchEntitlements]);

//   // Initial load: warm from cache if available, then revalidate
//   useEffect(() => {
//     const entBizId = business?.id || businessId;
//     if (entBizId) {
//       fetchEntitlements(entBizId, { useWarmCache: true }).catch(() => {});
//     } else {
//       setEntitlements(null);
//     }
//   }, [business?.id, businessId, fetchEntitlements]);

//   // ✅ DERIVED DISPLAY NAME for UI (Topbar, etc.)
//   // Keep permissions fresh after admin updates role permissions (MVP: refresh on focus/visibility)
//   const lastEntRefreshAtRef = useRef(0);
//   useEffect(() => {
//     const MIN_MS = 30 * 1000;

//     const maybeRefresh = () => {
//       const now = Date.now();
//       if (now - lastEntRefreshAtRef.current < MIN_MS) return;
//       lastEntRefreshAtRef.current = now;
//       refreshEntitlements().catch(() => {});
//     };

//     const onFocus = () => maybeRefresh();
//     const onVisibilityChange = () => {
//       if (document.visibilityState === "visible") maybeRefresh();
//     };

//     window.addEventListener("focus", onFocus);
//     document.addEventListener("visibilitychange", onVisibilityChange);
//     return () => {
//       window.removeEventListener("focus", onFocus);
//       document.removeEventListener("visibilitychange", onVisibilityChange);
//     };
//   }, [refreshEntitlements]);

//   const userName =
//     business?.businessName ||
//     business?.name ||
//     business?.companyName ||
//     user?.fullName ||
//     user?.name ||
//     user?.displayName ||
//     null;

//   const value = useMemo(
//     () => ({
//       // core
//       isLoading,
//       user,
//       business,
//       businessId, // ✅ exposed here for CampaignBuilder, ESU, etc.
//       role,
//       status,
//       hasAllAccess,
//       permissions,
//       availableFeatures,
//       can,
//       refreshAuthContext,
//       userName,
//       logout, // 👈 exposed here

//       // entitlements
//       entitlements,
//       entLoading,
//       entError,
//       hasFeature,
//       getQuota,
//       canUseQuota,
//       refreshEntitlements,
//     }),
//     [
//       isLoading,
//       user,
//       business,
//       businessId,
//       role,
//       status,
//       hasAllAccess,
//       permissions,
//       availableFeatures,
//       can,
//       refreshAuthContext,
//       userName,
//       logout,
//       entitlements,
//       entLoading,
//       entError,
//       hasFeature,
//       getQuota,
//       canUseQuota,
//       refreshEntitlements,
//     ]
//   );

//   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
// }
